import { A11yModule } from '@angular/cdk/a11y';
import { Component, computed, HostListener, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { AGENT_MAX_WIDTH, AGENT_MIN_WIDTH, AgentUiStore } from '../../services/agent-ui.store';

@Component({
  selector: 'agent-shell',
  standalone: true,
  imports: [MatButtonModule, A11yModule],
  templateUrl: './agent-shell.component.html',
  styleUrl: './agent-shell.component.scss'
})
export class AgentShellComponent {
  readonly available = input(false);
  readonly ui = inject(AgentUiStore);
  readonly viewportWidth = signal(window.innerWidth);
  readonly compact = computed(() => this.viewportWidth() <= 900);
  readonly effectiveWidth = computed(() => Math.min(this.ui.width(), this.maximumWidth()));
  readonly desktopDockOffset = computed(() =>
    this.available() && this.ui.open() && !this.compact() ? `${this.effectiveWidth()}px` : '0px');
  readonly minimumWidth = AGENT_MIN_WIDTH;
  readonly maximumWidth = computed(() => Math.min(AGENT_MAX_WIDTH, Math.max(AGENT_MIN_WIDTH, this.viewportWidth() * 0.55)));
  private drag: { pointerId: number; x: number; width: number } | null = null;

  @HostListener('window:resize')
  onWindowResize(): void {
    this.viewportWidth.set(window.innerWidth);
  }

  startResize(event: PointerEvent): void {
    if (event.button !== 0 || this.compact()) return;
    this.drag = { pointerId: event.pointerId, x: event.clientX, width: this.effectiveWidth() };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  resize(event: PointerEvent): void {
    if (!this.drag || this.drag.pointerId !== event.pointerId) return;
    this.ui.resize(Math.min(this.maximumWidth(), this.drag.width + this.drag.x - event.clientX));
  }

  endResize(event: PointerEvent): void {
    if (!this.drag || this.drag.pointerId !== event.pointerId) return;
    this.drag = null;
    this.ui.save();
  }

  resizeWithKeyboard(event: KeyboardEvent): void {
    const widths: Record<string, number> = {
      ArrowLeft: this.effectiveWidth() + 20,
      ArrowRight: this.effectiveWidth() - 20,
      Home: AGENT_MIN_WIDTH,
      End: this.maximumWidth()
    };
    if (!(event.key in widths)) return;
    event.preventDefault();
    this.ui.resize(Math.min(widths[event.key], this.maximumWidth()));
    this.ui.save();
  }
}
